/**
 * PrintTemplatesPage — `/printing/templates`.
 *
 * Lists print templates (`GET /v1/printing/templates`). Tapping a row deep-links
 * to the editor at `/printing/templates/:id`.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { listPrintTemplates } from '../../lib/api/printing';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function PrintTemplatesPage(): JSX.Element {
  const templates = useQuery({
    queryKey: ['printing', 'templates'],
    queryFn: () => listPrintTemplates({ pageSize: 100 }),
    retry: false,
  });

  const denied =
    templates.isError && templates.error instanceof ApiError && (templates.error.status === 401 || templates.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Print templates"
        description="Label + receipt + report templates."
        actions={
          <Link
            to="/printing/templates/new"
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 hover:bg-nipost-green-dark"
          >
            <Plus className="w-4 h-4" /> New template
          </Link>
        }
      />

      {templates.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Printing/read</code>.
        </DeferredNotice>
      ) : templates.isError ? (
        <DeferredNotice title="Service unreachable">{(templates.error as Error).message}</DeferredNotice>
      ) : (templates.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No templates yet.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Key</th>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Version</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {templates.data?.items.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{t.templateKey}</td>
                  <td className="px-4 py-2 text-xs">{t.kind}</td>
                  <td className="px-4 py-2 text-xs">v{t.version}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={t.active ? 'ACTIVE' : 'PENDING'} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/printing/templates/${encodeURIComponent(t.id)}`}
                      className="text-xs font-semibold text-nipost-green hover:underline"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
